import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import notificationsService from '../services/notifications';
import { inductionNotificationPath } from '../utils/inductionNotifications';
import { tilNotificationPath } from '../utils/tilNotifications';
import { staffWarningNotificationPath } from '../utils/staffWarningNotifications';
import { sensorNotificationPath } from '../utils/sensorNotifications';
import { salesNotificationPath } from '../utils/salesNotifications';

/** Where tapping a push notification opens. */
function pushNotificationPath(type: string | undefined, data?: Record<string, string>): string {
  if (type === 'timesheet_submitted' || type === 'leave_submitted' || type === 'missed_clock_declared' || type === 'timesheet_approval_reminder') return '/approvals';
  if (type === 'timesheet_approved' || type === 'timesheet_rejected') return '/timesheet/history';
  if (type === 'timesheet_reminder' || type === 'timesheet_submit_nudge') return '/timesheet';
  if (type === 'leave_approved' || type === 'leave_rejected') return '/leave';
  const induction = inductionNotificationPath(type, data);
  if (induction) return induction;
  const til = tilNotificationPath(type, data);
  if (til) return til;
  const warning = staffWarningNotificationPath(type, data);
  if (warning) return warning;
  // Insurance due or lapsed — the policy, which names its machines.
  if (type?.startsWith('equipment_insurance_') && data?.policy_id) return `/equipment/insurance/${data.policy_id}`;
  // Service due, loans overdue, rego expiring — straight to the machine.
  if (type?.startsWith('equipment_') && data?.equipment_id) return `/equipment/${data.equipment_id}`;
  if (type === 'po_receipt_added' && data?.po_id) return `/purchase-orders/${data.po_id}`;
  if (type?.startsWith('credit_note_request') && data?.request_id) return `/accounts/credit-note-requests?request=${data.request_id}`;
  if (type === 'nursery_sourcing_request') return '/nursery/material?tab=requests';
  if (type?.startsWith('spray_weather_') && data?.spray_id) return `/spray/${data.spray_id}`;
  if (type === 'irrigation_rain_skip' && data?.controller_id) return `/irrigation/espnow/${data.controller_id}?tab=map`;
  const sensor = sensorNotificationPath(type, data);
  if (sensor) return sensor;
  const sales = salesNotificationPath(type, data);
  if (sales) return sales;
  if (type === 'pest_alert') {
    const q = new URLSearchParams();
    if (data?.farm_id) q.set('farm', data.farm_id);
    if (data?.pest_model_id) q.set('pest', data.pest_model_id);
    const qs = q.toString();
    return `/weather/pests${qs ? `?${qs}` : ''}`;
  }
  return '/dashboard';
}

/**
 * Registers the device for FCM / APNs push notifications and wires up
 * foreground + tap handlers.
 *
 * Must be used inside a component that has access to React Router context
 * (i.e. inside <Router>).
 *
 * On web this is a no-op — push notifications only run on native builds.
 */
export function usePushNotifications() {
  const { isAuthenticated, user, switchOrg } = useAuth();
  const navigate = useNavigate();
  // Read at tap time without re-registering every listener when the user changes.
  const userRef = useRef(user);
  userRef.current = user;
  const switchOrgRef = useRef(switchOrg);
  switchOrgRef.current = switchOrg;

  useEffect(() => {
    // Only run on native Capacitor platforms (Android / iOS)
    if (!Capacitor.isNativePlatform()) return;
    if (!isAuthenticated) return;

    let cleanup: (() => void) | undefined;

    async function setup() {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Create a notification channel required for Android 8+ (Oreo and above)
        if (Capacitor.getPlatform() === 'android') {
          await PushNotifications.createChannel({
            id: 'default',
            name: 'Default',
            description: 'Default notification channel',
            importance: 5, // IMPORTANCE_HIGH — shows as heads-up notification
            visibility: 1, // VISIBILITY_PUBLIC
            vibration: true,
            sound: 'default',
          });
        }

        // Request permission
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive !== 'granted') {
          console.warn('Push notification permission not granted');
          return;
        }

        // Register with FCM / APNs
        await PushNotifications.register();

        // Receive the device token and send it to the backend
        const tokenListener = await PushNotifications.addListener(
          'registration',
          async (tokenData) => {
            const platform = Capacitor.getPlatform() as 'ios' | 'android';
            try {
              await notificationsService.registerPushToken(tokenData.value, platform);
            } catch (err) {
              console.error('Failed to register push token with server:', err);
            }
          }
        );

        // Log registration errors
        const registrationErrorListener = await PushNotifications.addListener(
          'registrationError',
          (err) => {
            console.error('Push registration error:', err);
          }
        );

        // Foreground (and background data-only) push handler.
        const foregroundListener = await PushNotifications.addListener(
          'pushNotificationReceived',
          async () => {
            // Refresh the unread notification badge.
            window.dispatchEvent(new CustomEvent('push-notification-received'));
          }
        );

        // Handle push notification tap (background / killed app)
        const actionListener = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (action) => {
            const data = action.notification.data as Record<string, string> | undefined;
            const path = pushNotificationPath(data?.type, data);
            // A push reaches every device whichever organisation it is for. Opening
            // it while the app shows another one would land on a record that
            // organisation does not have, so switch to the notification's first.
            const orgId = Number(data?.organisation_id);
            const current = userRef.current;
            const activeOrgId = current?.orgs?.find(o => o.is_active)?.id ?? current?.organisation_id;
            if (orgId && orgId !== activeOrgId && current?.orgs?.some(o => o.id === orgId)) {
              switchOrgRef.current(orgId)
                .then(() => navigate(path))
                .catch(() => navigate('/dashboard'));
            } else {
              navigate(path);
            }
          }
        );

        // Handle local notification tap (scheduled reminders)
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        type ActionPerformed = import('@capacitor/local-notifications').ActionPerformed;
        const localActionListener = await LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (action: ActionPerformed) => {
            const extra = action.notification.extra as Record<string, string> | undefined;
            if (extra?.type === 'timesheet_reminder') {
              navigate('/timesheet');
            }
          }
        );

        cleanup = () => {
          tokenListener.remove();
          registrationErrorListener.remove();
          foregroundListener.remove();
          actionListener.remove();
          localActionListener.remove();
        };
      } catch (err) {
        console.error('Push notification setup error:', err);
      }
    }

    setup();

    return () => {
      if (cleanup) cleanup();
    };
  }, [isAuthenticated, navigate]);
}
